import React, { useEffect, useRef, useState } from 'react';
import { useConfirm } from '../context/ConfirmContext';
import { useNavigate, useLocation } from 'react-router-dom';
import Pagination from './Pagination';
import { usePagination } from '../hooks/usePagination';
import { Activity } from '../types';
import ActivitiesTable from './ActivitiesTable';
import { activitiesAPI } from '../services/api';
import { addRedirectToUrl } from '../utils/redirectUtils';

interface ActivitiesListProps {
  referenceType: 'Project' | 'Customer';
  referenceId: number;
}

// A reference change starts a fresh list lifetime, including pagination and pending actions.
const ActivitiesList: React.FC<ActivitiesListProps> = (props) => (
  <ScopedActivitiesList key={`${props.referenceType}:${props.referenceId}`} {...props} />
);

const ScopedActivitiesList: React.FC<ActivitiesListProps> = ({ referenceType, referenceId }) => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [deleteError, setDeleteError] = useState(false);
  const [retry, setRetry] = useState(0);
  const lifetime = useRef<object | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const confirm = useConfirm();

  useEffect(() => {
    lifetime.current = {};
    return () => { lifetime.current = null; };
  }, []);

  useEffect(() => {
    let active = true;
    const loadActivities = async () => {
      setLoading(true);
      setLoadError(false);
      try {
        const data = await activitiesAPI.getActivitiesByReference(referenceType, referenceId);
        if (active) setActivities(data);
      } catch (err) {
        if (!active) return;
        setLoadError(true);
        console.error('Error loading activities:', err);
      } finally {
        if (active) setLoading(false);
      }
    };
    void loadActivities();
    return () => { active = false; };
  }, [referenceType, referenceId, retry]);

  const handleDelete = async (activityId: number) => {
    const actionLifetime = lifetime.current;
    const isCurrent = () => actionLifetime !== null && lifetime.current === actionLifetime;
    const isConfirmed = await confirm({
      title: 'Delete activity',
      message: 'Are you sure you want to delete this activity? This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger'
    });
    // Navigation may have happened while the confirmation dialog was open.
    if (!isConfirmed || !isCurrent()) return;

    setDeleteError(false);
    try {
      await activitiesAPI.deleteActivity(activityId);
      if (isCurrent()) setActivities(prev => prev.filter(a => a.id !== activityId));
    } catch (err) {
      if (!isCurrent()) return;
      setDeleteError(true);
      console.error('Failed to delete activity:', err);
    }
  };

  const handleCreate = () => {
    // Add redirect parameter to return to current location after creating activity
    const url = `/activities/${referenceType}/${referenceId}/create`;
    navigate(addRedirectToUrl(url, location.pathname + location.search));
  };

  const handleEdit = (activity: Activity) => {
    // Add redirect parameter to return to current location after editing activity
    const url = `/activities/${referenceType}/${referenceId}/edit/${activity.id}`;
    navigate(addRedirectToUrl(url, location.pathname + location.search));
  };

  const {
    currentPage,
    pageSize,
    paginatedData: paginatedActivities,
    totalItems,
    goToPage,
    setPageSize
  } = usePagination({ data: activities, initialPageSize: 5 });

  if (loading) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <div className="spinner-border" role="status" aria-label="Loading activities" />
        <span className="visually-hidden">Loading...</span>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="alert alert-danger" role="alert">
        <p>Unable to load activities. Please try again.</p>
        <button className="btn btn-secondary" onClick={() => setRetry(value => value + 1)}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="row">
      <div className="col-12">
        <div className="card mb-4">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Activities</h5>
            <button className="btn btn-success btn-sm" onClick={handleCreate}>
              Create Activity
            </button>
          </div>
          <div className="card-body">
            {deleteError && (
              <div className="alert alert-danger" role="alert">Unable to delete activity. Please try again.</div>
            )}
            {activities.length > 0 ? (
              <>
                <ActivitiesTable
                  activities={paginatedActivities}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
                <Pagination
                  currentPage={currentPage}
                  totalItems={totalItems}
                  itemsPerPage={pageSize}
                  onPageChange={goToPage}
                  onPageSizeChange={setPageSize}
                  pageSizeOptions={[5, 10, 25, 50]}
                />
              </>
            ) : (
              <div className="text-center">
                <p className="text-muted mb-3">No activities found.</p>
                <button className="btn btn-success" onClick={handleCreate}>
                  Create First Activity
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivitiesList;
